import {
  Component,
  effect,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CustomGridStackWidget } from '../../../services/grid-widget-config.service';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { WidgetHelperService } from '../../../services/widget/widget-helper.service';
import { ChartModule } from 'primeng/chart';
import { getState } from '@ngrx/signals';
import { ChartLegendComponent } from '../../molecules/chart-legend/chart-legend.component';
import { ExtendedChartData } from '../../../models/chartjs-extensions';
import { StyleClassModule } from 'primeng/styleclass';
import { Chart, ChartData } from 'chart.js';
import { DividerModule } from 'primeng/divider';
import { NgClass, NgIf } from '@angular/common';
import { debounceTime, Subject } from 'rxjs';
import { GridStore } from '../../../stores/grid/grid.state';

@Component({
  selector: 'app-base-chart',
  standalone: true,
  imports: [
    ChartModule,
    ChartLegendComponent,
    StyleClassModule,
    DividerModule,
    NgIf,
    NgClass,
  ],
  templateUrl: './base-chart.component.html',
  styleUrl: './base-chart.component.scss',
})
export class BaseChartComponent implements OnInit, OnDestroy {
  protected readonly widgetStore = inject(WidgetStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly dataLayerStore = inject(DataLayerStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly gridStore = inject(GridStore);
  private resizeObserver!: ResizeObserver;

  private observer!: IntersectionObserver;
  protected isVisible = false; // To ensure the chart initializes only once

  private resizeSubject = new Subject<void>();

  type: any = null;
  chartData!: ExtendedChartData;

  @ViewChild('chartComp', { static: true }) chartComp!: any;
  @ViewChild('chartCanvas', { static: true }) chartCanvas!: any;
  @Input() id!: string;
  chartInstance!: Chart; // Chart.js instance

  initialized = false;
  options: any;
  indicatorData: any;
  displayLegend = true;
  protected config: CustomGridStackWidget | null = null;

  constructor(private widgetHelperService: WidgetHelperService) {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      this.updateOrInitChart(widgetState);
    });
  }

  /**
   * Updates an existing chart or initializes it if not already initialized.
   * Determines the action based on the current state and widget configuration.
   *
   * @param {any} widgetState - The current state of the widget containing information
   *        such as rerender identifiers to decide whether to update or initialize the chart.
   * @return {void} No return value.
   */
  private updateOrInitChart(widgetState: any): void {
    if (!this.initialized || !this.chartData) {
      this.initChart();
      this.init();
    } else if (
      this.initialized &&
      (widgetState.rerenderId === this.id ||
        widgetState.rerenderIds.includes(this.id))
    ) {
      this.config = this.widgetStore.get(this.id);
      this.updateChart();
    }
  }

  /**
   * Initializes the widget by loading its configuration, type, options, and chart data.
   * If the widget ID is not defined, or the configuration/type cannot be retrieved, the method exits without initializing.
   * Configures chart data and marks the widget as initialized.
   * Also sets up a resize observer for the widget after a delay.
   *
   * @return {void} This method does not return a value.
   */
  protected init(): void {
    if (!this.id) return;

    this.config = this.widgetStore.get(this.id);
    this.type = this.widgetStore.getType(this.id);

    if (!this.config || !this.type) return;
    this.options = this.widgetHelperService.getBaseOptions(
      this.type,
      this.config?.widget
    );

    this.chartData = this.widgetStore.getChartData(this.id);

    this.initialized = true;
    this.isVisible = true;
    this.updateChart();
    queueMicrotask(() => {
      this.initializeResizeObserver();
    });
  }

  /**
   * Updates the options property by retrieving the base options from the widgetHelperService.
   * The options are determined based on the current type and widget configuration.
   *
   * @return {void} Does not return any value.
   */
  private updateOptions(): void {
    this.options = this.widgetHelperService.getBaseOptions(
      this.type,
      this.config?.widget
    );
  }

  /**
   * Updates the chart with new data, labels, and configurations. If the chart instance
   * does not exist, it initializes the chart. This method handles updating, adding,
   * or removing datasets to ensure the chart reflects the current data state.
   *
   * @return {void} This method does not return a value.
   */
  updateChart(): void {
    this.displayLegend = this.widgetHelperService.displayHtmlLegend(this.type);

    if (!this.chartInstance) {
      console.warn('Chart instance not found. Initializing chart.');
      this.initChart();
      return;
    }

    // Fetch the updated chart data
    const newData = this.getChartDataByType();
    if (!newData) return;

    // Update labels
    this.chartInstance.data.labels = newData.labels;

    // Handle datasets: Add, update, or remove to match newData.datasets
    // Loop through newData.datasets and update or add datasets

    newData.datasets.forEach((dataset: any, index: number) => {
      const currentDataset = this.chartInstance.data.datasets[index] as any;
      if (currentDataset && dataset) {
        // Update existing dataset properties
        currentDataset.data = dataset.data;
        currentDataset.label = dataset.label;

        // Optional: Update other dataset properties if needed
        if (dataset.backgroundColor) {
          currentDataset.backgroundColor = dataset.backgroundColor;
        }
        if (dataset.borderColor) {
          currentDataset.borderColor = dataset.borderColor;
        }
        if (dataset.group) {
          currentDataset.group = dataset.group;
        }
        if (dataset.label) {
          currentDataset.label = dataset.label;
        }
        if (dataset.pointRadius !== null && dataset.pointRadius !== undefined) {
          currentDataset.pointRadius = dataset.pointRadius;
        }
        if (dataset.tension !== null && dataset.tension !== undefined) {
          currentDataset.tension = dataset.tension;
        }
        if (dataset.borderWidth !== null && dataset.borderWidth !== undefined) {
          currentDataset.borderWidth = dataset.borderWidth;
        }
        if (dataset.type) {
          currentDataset.type = dataset.type;
        }
        if (dataset.labelKey) {
          currentDataset.labelKey = dataset.labelKey;
        }
        if (dataset.dataKeys) {
          currentDataset.dataKeys = dataset.dataKeys;
        }
      } else {
        // Add a new dataset if it doesn't exist
        this.chartInstance.data.datasets.push(dataset);
      }
    });

    // Remove any excess datasets if newData.datasets is shorter than current datasets
    const excessDatasetCount =
      this.chartInstance.data.datasets.length - newData.datasets.length;
    if (excessDatasetCount > 0) {
      this.chartInstance.data.datasets.splice(
        newData.datasets.length,
        excessDatasetCount
      );
    }

    this.chartData = newData;
    this.updateOptions();
    this.chartInstance.options = this.options;

    // Update the chart with animation
    this.chartInstance.update();
  }

  getChartDataByType() {
    const chartData = this.widgetStore.getChartData(this.id);

    switch (this.type) {
      case 'polarArea':
        chartData.datasets = [chartData.datasets.at(0)];
        return chartData;
      default:
        return chartData;
    }
  }

  /**
   * Angular lifecycle hook that is called after data-bound properties of a directive are initialized.
   * This method initializes the chart component by invoking the `initChart` method.
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.initChart();
  }

  /**
   * Initializes the chart instance with the provided data, type, and configuration options.
   * This method checks if all required properties are available before creating the chart.
   *
   * @return {void} This method does not return a value.
   */
  initChart(): void {
    if (!this.chartCanvas || !this.chartData || !this.options) {
      return;
    }

    this.chartInstance = new Chart(this.chartCanvas.nativeElement, {
      type: this.type || 'bar', // Chart type
      data: this.chartData as ChartData<typeof this.type>, // Chart data
      options: {
        ...this.options,
        //animation: false, // Disable animations to boost rendering performance
        responsive: true,
        maintainAspectRatio: false,
      },
    });
  }

  /**
   * Angular lifecycle hook that is invoked when the component or directive is destroyed.
   * Cleans up resources such as the ResizeObserver and destroys the chart instance
   * to prevent memory leaks and ensure proper teardown of the component.
   *
   * @return {void} No return value.
   */
  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.chartInstance?.destroy();
  }

  /**
   * override to use
   */
  resizeCallBack() {}

  /**
   * Initializes a ResizeObserver instance to monitor size changes of the parent element
   * of the chart component. If the chart component changes size, the resize callback
   * will be executed. Ensures the observer is only initialized once.
   *
   * @return {void} Does not return any value.
   */
  initializeResizeObserver(): void {
    if (this.resizeObserver) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.resizeSubject.next(); // Emit resize events
    });

    this.resizeSubject.pipe(debounceTime(100)).subscribe(() => {
      this.resizeCallBack(); // Execute only after debounce
    });

    // Observe parent element's size
    const element = this.chartComp?.el || this.chartComp;
    this.resizeObserver.observe(element.nativeElement.parentElement!);
  }

  /**
   * Handles the toggling of a dataset, performing any necessary actions
   * such as saving the state or updating configurations.
   *
   * @param {any} dataset - The dataset that is being toggled.
   * @return {void} - No return value.
   */
  onDatasetToggled(dataset: any): void {
    if (!this.config) return;
    if (!this.config.widget.disabledDatasets)
      this.config.widget.disabledDatasets = [];
    const idx = this.config?.widget.disabledDatasets.findIndex(
      id => id === dataset.labelKey
    );
    if (idx !== -1) {
      this.config.widget.disabledDatasets.splice(idx, 1);
    } else {
      this.config.widget.disabledDatasets.push(dataset.labelKey);
    }

    this.widgetStore.update(this.id, this.config.widget, true);
    this.gridStore.triggerLayoutChanges();
  }

  /**
   * Handles the toggling of a dataset, performing any necessary actions
   * such as saving the state or updating configurations.
   *
   * @param {any} key - The dataset that is being toggled.
   * @return {void} - No return value.
   */
  onDataPointToggled(key: string): void {
    if (!this.config) return;
    if (!this.config.widget.disabledDataPoints)
      this.config.widget.disabledDataPoints = [];

    const idx = this.config?.widget.disabledDataPoints.findIndex(
      id => id === key
    );

    if (idx !== -1) {
      this.config.widget.disabledDataPoints.splice(idx, 1);
    } else {
      this.config.widget.disabledDataPoints.push(key);
    }

    this.widgetStore.update(this.id, this.config.widget, true);
    this.gridStore.triggerLayoutChanges();
  }
}
